<template>
	<div class="tiptap-rich-editor-container" :style="{ minHeight: minHeight + 'px' }">
		<editor-content :editor="editor" class="editor-content-area" />
	</div>
</template>

<script setup>
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
// 导入常用的扩展
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import TextStyle from '@tiptap/extension-text-style'
import StarterKit from '@tiptap/starter-kit'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import { ref, onMounted, onBeforeUnmount, watch, defineExpose } from 'vue'

// 定义组件接收的 props
const props = defineProps({
	modelValue: {
		type: String,
		default: ''
	},
	editable: {
		type: Boolean,
		default: true
	},
	minHeight: {
		type: Number,
		default: 300
	},
	// 可以通过 props 传入自定义的 Tiptap 扩展
	extensions: {
		type: Array,
		default: () => []
	},
	// Tiptap 编辑器的额外配置
	editorOptions: {
		type: Object,
		default: () => ({})
	}
})

// 定义组件可以发出的事件
const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'update'])

// 使用 useEditor hook 初始化 Tiptap 编辑器
const editor = useEditor({
	content: props.modelValue, // 初始内容
	editable: props.editable, // 是否可编辑
	extensions: [
		StarterKit.configure({
			// 禁用 StarterKit 中某些默认包含的扩展，如果你想单独配置它们
			// 例如，如果你想使用 @tiptap/extension-heading 来更精细地控制标题级别
			// heading: false,
			// bold: false,
			// italic: false,
			// ...
		}),
		Image.configure({
			inline: true, // 允许图片作为文本的一部分
			allowBase64: true // 允许粘贴 base64 图片 (不推荐用于生产，图片文件较大)
			// 其他配置，如 customUploadFile
			// customUploadFile: (file) => {
			//   // 在这里实现你的文件上传逻辑，返回 Promise<string> (图片 URL)
			// }
		}),
		Link.configure({
			openOnClick: false, // 不点击链接时打开
			autolink: true // 自动将 URL 文本转换为链接
		}),
		Table.configure({
			resizable: true // 允许表格列调整大小
		}),
		TableCell,
		TableHeader,
		TableRow,
		Highlight.configure({
			multicolor: true // 允许多种高亮颜色
		}),
		TextStyle, // 需要 TextStyle 才能使用 Color 扩展
		Color,
		// ...你传入的额外扩展
		...props.extensions
	],
	onUpdate: ({ editor }) => {
		// 编辑器内容更新时，发出 'update:modelValue' 事件以支持 v-model
		// 并且发出 'update' 事件，如果父组件需要更细粒度的控制
		emit('update:modelValue', editor.getHTML())
		emit('update', editor.getHTML())
	},
	onFocus: () => emit('focus'),
	onBlur: () => emit('blur'),
	...props.editorOptions // 传入额外的 Tiptap 配置
})

// 监听 props.modelValue 变化，同步外部内容到编辑器
watch(
	() => props.modelValue,
	(newVal) => {
		if (editor.value && newVal !== editor.value.getHTML()) {
			editor.value.commands.setContent(newVal, false) // false 表示不合并历史记录
		}
	}
)

// 监听 props.editable 变化，同步编辑器可编辑状态
watch(
	() => props.editable,
	(newVal) => {
		if (editor.value) {
			editor.value.setEditable(newVal)
		}
	}
)

// 在组件挂载后，确保编辑器初始化并设置初始可编辑状态
onMounted(() => {
	if (editor.value) {
		editor.value.setEditable(props.editable)
	}
})

// 组件销毁前销毁编辑器实例
onBeforeUnmount(() => {
	if (editor.value) {
		editor.value.destroy()
	}
})

// 将编辑器实例暴露给父组件，以便父组件可以操作它（例如构建工具栏）
defineExpose({
	editor
})
</script>

<style lang="scss" scoped>
.tiptap-rich-editor-container {
	border: 1px solid #ddd;
	border-radius: 4px;
	overflow: hidden;
	display: flex;
	flex-direction: column;

	/* Tiptap 默认样式 */
	:deep(.ProseMirror) {
		padding: 10px;
		outline: none;
		line-height: 1.5;
		flex-grow: 1;
		overflow-y: auto; /* 确保内容超出时可滚动 */

		/* 样式穿透：确保内容区域的最小高度 */
		min-height: 100%; /* 继承容器的 minHeight */
	}

	/* 针对只读模式的样式 */
	:deep(.ProseMirror:focus) {
		outline: none;
	}

	:deep(.ProseMirror.ProseMirror-focused) {
		/* 你可以在这里添加聚焦时的边框或阴影 */
	}

	:deep(.ProseMirror p.is-editor-empty:first-child::before) {
		content: attr(data-placeholder);
		float: left;
		color: #adb5bd;
		pointer-events: none;
		height: 0;
	}

	/* 图片、链接、表格等扩展的默认样式 */
	:deep(img) {
		max-width: 100%;
		height: auto;
	}

	:deep(a) {
		color: #1a73e8;
		text-decoration: underline;
	}

	:deep(table) {
		width: 100%;
		border-collapse: collapse;
		margin: 10px 0;
	}

	:deep(th),
	:deep(td) {
		border: 1px solid #ccc;
		padding: 8px;
		text-align: left;
	}

	:deep(th) {
		background-color: #f0f0f0;
	}

	/* 高亮样式 */
	:deep(.has-focus) {
		background-color: rgba(64, 158, 255, 0.2); /* 蓝色高亮 */
	}
	:deep(.has-focus.yellow) {
		background-color: rgba(255, 230, 0, 0.3); /* 黄色高亮 */
	}
	:deep(.has-focus.red) {
		background-color: rgba(255, 0, 0, 0.2); /* 红色高亮 */
	}
	:deep(.has-focus.green) {
		background-color: rgba(0, 128, 0, 0.2); /* 绿色高亮 */
	}
}
</style>
